import { AlertCircle, Clock, Trash2 } from 'lucide-react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface DraftRecoveryDialogProps {
  /** 是否显示对话框 */
  open: boolean
  /** 关闭对话框回调 */
  onOpenChange: (open: boolean) => void
  /** 草稿保存时间（可以是 number 或 Date） */
  savedAt: number | Date | null
  /** 恢复草稿回调 */
  onRecover: () => void
  /** 丢弃草稿回调 */
  onDiscard: () => void
  /** 表单类型名称（用于显示） */
  formTypeName?: string
}

/**
 * 草稿恢复对话框
 * Requirements: 12.2, 12.5
 */
export function DraftRecoveryDialog({
  open,
  onOpenChange,
  savedAt,
  onRecover,
  onDiscard,
  formTypeName = '表单',
}: DraftRecoveryDialogProps) {
  // Convert Date to timestamp if needed
  const timestamp = savedAt instanceof Date ? savedAt.getTime() : savedAt
  const formatSavedTime = (timestamp: number | null) => {
    if (!timestamp) {return '未知时间'}

    const date = new Date(timestamp)
    const now = new Date()
    const diff = now.getTime() - timestamp

    // 今天
    if (date.toDateString() === now.toDateString()) {
      return `今天 ${date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`
    }

    // 昨天
    const yesterday = new Date(now)
    yesterday.setDate(yesterday.getDate() - 1)
    if (date.toDateString() === yesterday.toDateString()) {
      return `昨天 ${date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`
    }

    // 一周内
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      const days = Math.floor(diff / (24 * 60 * 60 * 1000))
      return `${days} 天前`
    }

    // 更早
    return date.toLocaleDateString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const handleRecover = () => {
    onRecover()
    onOpenChange(false)
  }

  const handleDiscard = () => {
    onDiscard()
    onOpenChange(false)
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-blue-500" />
            发现未完成的{formTypeName}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>您有一份未提交的{formTypeName}草稿，是否要恢复？</p>
            {timestamp && (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                保存于 {formatSavedTime(timestamp)}
              </p>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={handleDiscard}
            className="flex items-center gap-1.5"
          >
            <Trash2 className="h-4 w-4" />
            丢弃草稿
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleRecover}>
            恢复草稿
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
